import { QueryKeys } from "@/constants/Query";
import { type ApiError, useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type {
  CreateGenericObservationRequest,
  GroupItemAnswerType,
} from "@/types/observations";
import type { ObservationGroupItem } from "@/types/observations";
import { useQueryClient } from "@tanstack/react-query";

interface GetGenericObservationsResponse {
  items: ObservationGroupItem[];
}

export const useGetGenericObservations = (
  patientId: string | undefined,
  group: string,
) => {
  return useApi<GetGenericObservationsResponse>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey: QueryKeys.Patient.MedicalRecords.GenericObservations.DEFAULT(
        patientId,
        group,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/observation-generic/group-items",
          {
            params: { patientId, group },
          },
        );
        return response?.data;
      },
    },
  });
};

export const useCreateGenericObservation = ({
  patientId,
  group,
  onSuccess,
  onError,
}: {
  patientId: string;
  group: string;
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.MedicalRecords.GenericObservations.CREATE(
        patientId,
        group,
      ),
      mutationFn: async (data: CreateGenericObservationRequest) => {
        const response = await axiosInstance.post(
          "/charting/observation-generic/",
          data,
        );
        return response?.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.GenericObservations.DEFAULT(
              patientId,
              group,
            ),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface GetGenericObservationHistoryResponse {
  observations: ObservationHistoryItem[];
}

export interface ObservationHistoryItem {
  observationDate: string;
  observation: ObservationGroupItem["documentedObservation"];
  groupItemAnswerType: GroupItemAnswerType;
}

export const useGetGenericObservationHistory = ({
  code,
  enabled = true,
  group, // group is necessary for the query key for invalidation purposes
  patientId,
  system,
}: {
  code: string;
  enabled?: boolean;
  group: string;
  patientId: string;
  system: string;
}) => {
  const systemShortString = system.includes("snomed") ? "snomed" : "loinc";
  return useApi<GetGenericObservationHistoryResponse>({
    queryOptions: {
      enabled: Boolean(patientId && enabled),
      queryKey: QueryKeys.Patient.MedicalRecords.GenericObservations.HISTORY(
        patientId,
        group,
        code,
      ),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/observation-generic/history",
          {
            params: { patientId, code, system: systemShortString },
          },
        );
        return response?.data;
      },
    },
  });
};
